import React, { useRef, useState } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Upload, Link2, X, Pencil, Trash2, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageUploadInput — single-image picker for admin forms.
 *
 * Surface area:
 *   - Two input modes: file upload (default) and paste URL
 *   - Multi-file support — every successful upload calls `onChange(url)` so a
 *     parent gallery can accumulate images
 *   - Prominent Replace + Delete buttons when a value is set (the X used to be
 *     a tiny black square that was easy to miss — see iter 30)
 *   - Two-click confirmation on Delete: first click arms the button (turns red),
 *     second click deletes. Click anywhere else to cancel
 *
 * Backwards-compatible — keeps the same props: `value`, `onChange`, `testId`,
 * `aspect`, `fallbackText`, `hint`, `multiple`.
 */
export default function ImageUploadInput({
    label, value, onChange, testId, aspect = "1/1", fallbackText, hint, multiple = true,
}) {
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState("upload"); // 'upload' | 'url'
    const [confirmDelete, setConfirmDelete] = useState(false);
    const fileRef = useRef(null);

    const handleFile = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setBusy(true);
        let okCount = 0;
        let failCount = 0;
        for (const file of files) {
            if (file.size > 6 * 1024 * 1024) {
                toast.error(`${file.name}: must be under 6 MB`);
                failCount++;
                continue;
            }
            try {
                const fd = new FormData();
                fd.append("file", file);
                // eslint-disable-next-line no-await-in-loop
                const { data } = await api.post("/uploads", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                onChange(`${BACKEND_URL}${data.url}`);
                okCount++;
            } catch (err) {
                toast.error(`${file.name}: ${err.response?.data?.detail || "Upload failed"}`);
                failCount++;
            }
        }
        if (okCount > 0) {
            toast.success(okCount === 1 ? "Uploaded" : `Uploaded ${okCount} images${failCount ? ` (${failCount} failed)` : ""}`);
        }
        setBusy(false);
        setConfirmDelete(false);
        if (fileRef.current) fileRef.current.value = "";
    };

    const triggerReplace = () => {
        setConfirmDelete(false);
        setMode("upload");
        fileRef.current?.click();
    };

    const triggerDelete = () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            // auto-cancel after 4 seconds so the red state doesn't linger
            setTimeout(() => setConfirmDelete(false), 4000);
            return;
        }
        onChange("");
        setConfirmDelete(false);
        toast.success("Image removed");
    };

    const aspectStyle = { aspectRatio: aspect.replace("/", " / ") };
    const previewSize = aspect === "4/5" ? 96 : aspect === "5/4" ? 120 : 80;

    return (
        <div data-testid={testId} onMouseLeave={() => setConfirmDelete(false)}>
            {label && <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>}

            <div className="flex gap-1 mt-1 mb-2">
                <button
                    type="button"
                    onClick={() => setMode("upload")}
                    className={`text-[10px] uppercase tracking-widest px-3 py-1 border ${mode === "upload" ? "bg-sky-700 text-stone-50 border-sky-700" : "border-stone-300 text-stone-600"}`}
                    data-testid={testId ? `${testId}-mode-upload` : undefined}
                >
                    <Upload className="w-3 h-3 inline mr-1" />Upload
                </button>
                <button
                    type="button"
                    onClick={() => setMode("url")}
                    className={`text-[10px] uppercase tracking-widest px-3 py-1 border ${mode === "url" ? "bg-sky-700 text-stone-50 border-sky-700" : "border-stone-300 text-stone-600"}`}
                    data-testid={testId ? `${testId}-mode-url` : undefined}
                >
                    <Link2 className="w-3 h-3 inline mr-1" />Paste URL
                </button>
            </div>

            {mode === "url" ? (
                <Input
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://… (any public image URL)"
                    className="rounded-none border-stone-300"
                    data-testid={testId ? `${testId}-url` : undefined}
                />
            ) : (
                <div className="flex gap-2">
                    <input ref={fileRef} type="file" accept="image/*" multiple={multiple} onChange={handleFile} className="hidden" data-testid={testId ? `${testId}-file` : undefined} />
                    <Button type="button" onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="rounded-none border-stone-400 flex-1 justify-start" data-testid={testId ? `${testId}-upload-btn` : undefined}>
                        <Upload className="w-4 h-4 mr-2" />{busy ? "Uploading…" : (multiple ? "Choose images (multi-select, ≤ 6 MB each)" : "Choose image (≤ 6 MB)")}
                    </Button>
                </div>
            )}

            <div className="mt-3 flex items-start gap-3">
                <div className="border border-stone-200 bg-stone-50 overflow-hidden shrink-0 relative group" style={{ width: previewSize, ...aspectStyle }}>
                    {value ? (
                        <>
                            <img src={value} alt="preview" className="w-full h-full object-cover" />
                        </>
                    ) : (
                        <div className="w-full h-full grid place-items-center text-stone-400 text-xs">{fallbackText || "preview"}</div>
                    )}
                </div>
                <div className="flex-1">
                    {value && (
                        <>
                            <div className="flex flex-wrap gap-2 mb-2">
                                <Button
                                    type="button"
                                    onClick={triggerReplace}
                                    variant="outline"
                                    size="sm"
                                    className="rounded-none border-stone-300 h-8 px-3 text-xs"
                                    data-testid={testId ? `${testId}-replace` : undefined}
                                >
                                    <Pencil className="w-3.5 h-3.5 mr-1.5" />Replace
                                </Button>
                                <Button
                                    type="button"
                                    onClick={triggerDelete}
                                    variant="outline"
                                    size="sm"
                                    className={`rounded-none h-8 px-3 text-xs transition-colors ${confirmDelete ? "bg-red-700 border-red-700 text-white hover:bg-red-800" : "border-red-300 text-red-700 hover:bg-red-50"}`}
                                    data-testid={testId ? `${testId}-delete` : undefined}
                                >
                                    {confirmDelete ? <><Check className="w-3.5 h-3.5 mr-1.5" />Confirm delete</> : <><Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete</>}
                                </Button>
                                {confirmDelete && (
                                    <Button
                                        type="button"
                                        onClick={() => setConfirmDelete(false)}
                                        variant="ghost"
                                        size="sm"
                                        className="rounded-none h-8 px-2 text-xs text-stone-500"
                                        data-testid={testId ? `${testId}-cancel-delete` : undefined}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </div>
                            {confirmDelete && (
                                <p className="text-[11px] text-red-700 flex items-center gap-1 mb-1">
                                    <AlertTriangle className="w-3 h-3" />Click delete again to remove this image
                                </p>
                            )}
                            <p className="text-[10px] text-stone-500 break-all max-w-md">{value}</p>
                        </>
                    )}
                    {hint && <p className="text-xs text-stone-500 mt-1">{hint}</p>}
                    {!hint && <p className="text-xs text-stone-500 mt-1">Aspect: <code>{aspect}</code> · png/jpg/webp/svg, max 6 MB</p>}
                </div>
            </div>
        </div>
    );
}

// Re-export the X icon so callers that previously imported it from this module keep working
export { X };
